import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import OrgSetup from "@/components/OrgSetup";
import { Button } from "@/components/ui/button";
import { Briefcase, FileText, Mail, MessageSquare, Plus, Loader2, Ruler, Calculator, Link2, FileSpreadsheet, Calendar, Zap, Bot, TrendingUp, Settings, Sparkles, Target, Satellite, Brain, Upload, Shield, Copy, Check, Layers, Building2, Clock, AlertCircle, Phone, DollarSign, Users, BarChart3, TrendingDown, Eye } from "lucide-react";
import { toast } from "sonner";
import { useWorkflows } from "@/hooks/useWorkflows";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { RecentActivity } from "@/components/RecentActivity";
import { StormWatchWidget } from "@/components/dashboard/StormWatchWidget";
import { NextActionCard } from "@/components/dashboard/NextActionCard";
import { SystemHealthWidget } from "@/components/dashboard/SystemHealthWidget";
import { TopLeadsCard } from "@/components/dashboard/TopLeadsCard";
import { DailySidebar } from "@/components/dashboard/DailySidebar";
import { useJobs } from "@/hooks/useJobs";
import { useLeads } from "@/hooks/useLeads";
import { cn } from "@/lib/utils";
import { MaintenanceAgreementDialog } from "@/components/commercial/MaintenanceAgreementDialog";
import { useInsuranceDashboardStats } from "@/hooks/useInsuranceDashboardStats";
import { useCommercialDashboardStats } from "@/hooks/useCommercialDashboardStats";
import { CoreCutDialog } from "@/components/commercial/CoreCutDialog";
import { SafetyPlanDialog } from "@/components/commercial/SafetyPlanDialog";
import { CallMonitorWidget } from "@/components/call-monitor/CallMonitorWidget";
import { useCallMonitorContext } from "@/contexts/CallMonitorContext";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { RevenueChart } from "@/components/dashboard/RevenueChart";
import { PipelineChart } from "@/components/dashboard/PipelineChart";
import { AIInsightsPanel } from "@/components/dashboard/AIInsightsPanel";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileDashboard } from "@/components/mobile/MobileDashboard";
import { useTrade } from "@/contexts/TradeContext";
import { DashboardPanel } from "@/components/dashboard/DashboardPanel";

const Dashboard = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const {
    user,
    orgId,
    isPlatformOwner,
    loading: authLoading
  } = useAuth();
  const {
    jobs,
    loading: jobsLoading
  } = useJobs(orgId);
  const {
    leads,
    loading: leadsLoading
  } = useLeads(orgId);
  const { stats: insuranceStats, loading: insuranceStatsLoading } = useInsuranceDashboardStats(orgId);
  const { stats: commercialStats, loading: commercialStatsLoading } = useCommercialDashboardStats(orgId);
  const { t, isFeatureVisible, activeTradePack } = useTrade();
  const showInsurance = isFeatureVisible("insurance");
  const jobsLabel = t("jobs", "Jobs");
  // Trade-driven job-source segments. Falls back to roofing-style tabs only if the
  // active trade explicitly defines them; insurance is additionally gated by feature flag.
  const tradeSegments = (activeTradePack?.job_segments ?? []).filter(
    (s) => s.key !== "insurance" || showInsurance,
  );
  const showSegmentTabs = tradeSegments.length > 0;
  const [userName, setUserName] = useState<string>("");
  const [activeView, setActiveView] = useState<string>(
    tradeSegments[0]?.key ?? "retail",
  );

  // Keep activeView valid whenever the active trade (and therefore its segments) changes.
  useEffect(() => {
    if (!showSegmentTabs) {
      setActiveView("retail");
      return;
    }
    if (!tradeSegments.some((s) => s.key === activeView)) {
      setActiveView(tradeSegments[0].key);
    }
  }, [activeTradePack?.trade_code, showSegmentTabs, tradeSegments, activeView]);
  const [scopeAnalysis, setScopeAnalysis] = useState<any>(null);
  const [analyzingScope, setAnalyzingScope] = useState(false);
  const [rebuttalDialogOpen, setRebuttalDialogOpen] = useState(false);
  const [selectedRebuttal, setSelectedRebuttal] = useState<any>(null);
  const [copiedRebuttal, setCopiedRebuttal] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [coreCutDialogOpen, setCoreCutDialogOpen] = useState(false);
  const [safetyPlanDialogOpen, setSafetyPlanDialogOpen] = useState(false);
  const {
    setContext: setCallContext,
    clearContext: clearCallContext
  } = useCallMonitorContext();

  // NOTE: Property managers and bid deadlines are loaded from real data
  // (jobs + calendar_events) into `propertyManagers` and `bidDeadlines` state below.
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };
  const getTimeUntilDeadline = (deadline: Date) => {
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (hours < 24) return `${hours}h left`;
    return `${days}d left`;
  };

  // Hardcoded professional rebuttals - instant, no API needed
  const COMMON_REBUTTALS = [{
    id: 'ice-water',
    title: 'Ice & Water Shield (Code)',
    icon: Shield,
    color: 'neon-cyan',
    rebuttal: `Per International Residential Code (IRC) R905.2.8.5, Ice and Water Shield is REQUIRED at eaves in areas where there has been a history of ice forming along the eaves causing a backup of water. This property is located in a climate zone where ice damming is prevalent and documented.

The insurance carrier's scope fails to include this MANDATORY code requirement. This is not an upgrade or betterment - it is a code-mandated life safety item required for proper installation per manufacturer specifications and local building codes.

We are requesting immediate approval for Ice & Water Shield installation covering a minimum of 24 inches from the exterior wall line, as required by IRC and local building officials.

Supporting Documentation:
- IRC Section R905.2.8.5
- Local Building Department Requirements
- Manufacturer Installation Guidelines (attached)`
  }, {
    id: 'overhead-profit',
    title: 'Overhead & Profit (O&P)',
    icon: Calculator,
    color: 'neon-green',
    rebuttal: `Per Xactimate guidelines and industry standard practice, Overhead & Profit (O&P) is REQUIRED when three or more trades are involved in the restoration process OR when the project involves coordination and supervision of multiple subcontractors.

This claim clearly meets the threshold for O&P inclusion:
- Trade #1: Roofing contractor
- Trade #2: Siding/exterior contractor  
- Trade #3: Carpentry/framing contractor
- Trade #4: [Additional trade as applicable]

The denial of O&P on this claim is inconsistent with Xactimate's own published guidelines, insurance industry standards, and multiple case law precedents including:
- Cimino v. Allstate (establishing O&P on multi-trade losses)
- Multiple Department of Insurance rulings supporting O&P inclusion

We are requesting immediate approval for 10% Overhead and 10% Profit on all labor and materials, consistent with industry standards and your carrier's own estimating platform guidelines.`
  }, {
    id: 'ridge-vent',
    title: 'Ridge Vent Ventilation',
    icon: TrendingUp,
    color: 'neon-orange',
    rebuttal: `Per IRC Section R806.2, adequate ventilation is REQUIRED for all enclosed attic spaces. The existing roof system includes ridge vent ventilation as part of its engineered ventilation system. Removal of this system without proper replacement would:

1. Violate building code requirements for attic ventilation
2. Void manufacturer warranties on all roofing materials
3. Create conditions conducive to moisture damage, mold growth, and premature roof system failure
4. Result in a non-code-compliant roof installation

The insurance carrier's scope indicates "remove and dispose" of the existing ridge vent but fails to include replacement. This creates a direct code violation and would result in permit rejection by local building officials.

We are requesting immediate approval for replacement of ridge vent ventilation to maintain code compliance and proper roof system function. This is not a betterment - it is maintenance of the existing, required ventilation system.

Alternative: If carrier wishes to pursue a different ventilation strategy, we will require written approval from both the local building department AND the roofing material manufacturer confirming code compliance and warranty preservation.`
  }, {
    id: 'drip-edge',
    title: 'Drip Edge Requirement',
    icon: FileText,
    color: 'neon-purple',
    rebuttal: `Per International Residential Code (IRC) R905.2.8.5, Drip Edge is REQUIRED at eaves and gables for all asphalt shingle roof installations. This is not optional, negotiable, or subject to the adjuster's interpretation - it is a mandatory code requirement.

The insurance carrier's scope omits this required component. Installation of a roof system without code-required drip edge will result in:
1. Automatic permit rejection by local building officials
2. Immediate void of all manufacturer warranties
3. Non-compliant installation subject to teardown and reinstall
4. Potential water intrusion and fascia damage

Furthermore, Xactimate coding guidelines specifically include drip edge as a standard inclusion in roof replacement estimates. The carrier's omission is inconsistent with their own estimating platform standards.

We are requesting immediate approval for drip edge installation at all eaves and gables as required by IRC, manufacturer specifications, and local building codes. The alternative is a non-compliant, non-warrantied roof installation that will be rejected at final inspection.

Reference Documents:
- IRC Section R905.2.8.5
- [Manufacturer] Installation Manual (attached)
- Local Building Permit Requirements (attached)`
  }, {
    id: 'matching-shingles',
    title: 'Matching Shingles (ITEL)',
    icon: Target,
    color: 'neon-blue',
    rebuttal: `The damaged shingles are a discontinued product no longer available from the manufacturer. Per your policy's Matching and Uniformity provision and applicable state regulations, when materials of like kind and quality cannot be obtained, the carrier is obligated to replace enough additional material to achieve a reasonably uniform appearance.

Current Situation:
- Original Product: [Manufacturer/Product Name]
- Status: DISCONTINUED - confirmed by manufacturer (letter attached)
- Damaged Area: [Location/square footage]
- Visible Area Impact: Partial replacement would result in obvious mismatch visible from ground level

The "Similar But Not Matching" approach suggested by the carrier creates:
1. Obvious aesthetic mismatch reducing property value
2. Potential appraisal issues affecting marketability
3. Clear violation of policy matching provisions
4. Inconsistent with ITEL (Insurance To Exact Loss) regulations in this state

Case Law Support:
- [State] follows the "Reasonably Uniform Appearance" standard
- Multiple DOI rulings supporting full-slope replacement when matching is impossible
- Carrier precedent on similar claims with discontinued products

We are requesting immediate approval for replacement of [affected area/slope/entire roof] to achieve a reasonably uniform appearance as required by policy language and state regulations. The alternative approach of mismatched products is unacceptable and inconsistent with your policy obligations.

Attached Documentation:
- Manufacturer discontinuation letter
- Comparable product analysis showing no reasonable match available
- Photos documenting current roof appearance
- State ITEL regulation reference`
  }];
  const handleRebuttalClick = (rebuttal: any) => {
    setSelectedRebuttal(rebuttal);
    setRebuttalDialogOpen(true);
    setCopiedRebuttal(false);
  };
  const handleCopyRebuttal = async () => {
    if (selectedRebuttal) {
      try {
        await navigator.clipboard.writeText(selectedRebuttal.rebuttal);
        setCopiedRebuttal(true);
        toast.success('Rebuttal copied to clipboard!');
        setTimeout(() => setCopiedRebuttal(false), 2000);
      } catch (err) {
        toast.error('Failed to copy to clipboard');
      }
    }
  };
  const [stats, setStats] = useState({
    activeProjects: 0,
    projectsValue: 0,
    pendingProposals: 0,
    proposalsSent: 0,
    monthlyRevenue: 0,
    closeRate: 0,
    activeCampaigns: 0,
    // Retail-specific stats
    closeRateThisWeek: 0,
    closeRateTrend: 'up' as 'up' | 'down',
    avgTicketPrice: 0,
    financingApprovalRate: 0,
    leadsNeedingFollowUp: [] as any[],
    leadsBySource: {
      'Google LSA': 0,
      'Facebook': 0,
      'Canvas': 0,
      'Other': 0
    },
    // Insurance-specific stats (will be replaced by hook data)
    recoverableDepreciation: 0,
    activeSupplements: 0,
    supplementsValue: 0,
    avgClaimIncrease: 0
  });
  const [pipelineStages, setPipelineStages] = useState<Record<string, number>>({});
  const [propertyManagers, setPropertyManagers] = useState<any[]>([]);
  const [bidDeadlines, setBidDeadlines] = useState<any[]>([]);

  // Load workflow for current view (read-only for pipeline display).
  // Custom trade segments fall back to the retail workflow until per-segment workflows exist.
  const workflowType: "retail" | "insurance" | "commercial" =
    activeView === "insurance" || activeView === "commercial" ? activeView : "retail";
  const {
    workflow,
    loading: workflowLoading,
  } = useWorkflows(orgId || "", workflowType);
  const statsLoading = jobsLoading || leadsLoading;
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);
  useEffect(() => {
    if (user && orgId) {
      // Load user name
      supabase.from('profiles').select('full_name, email').eq('id', user.id).single().then(({
        data: profile
      }) => {
        if (profile) {
          setUserName(profile.full_name || profile.email?.split('@')[0] || "");
        }
      });
    }
  }, [user, orgId]);

  // Set call monitor context for dashboard
  useEffect(() => {
    setCallContext({
      activeJobId: null,
      activeLeadId: null,
      callMode: 'new-lead'
    });
    return () => {
      clearCallContext();
    };
  }, [setCallContext, clearCallContext]);

  // Update stats when insurance/commercial stats load
  useEffect(() => {
    if (activeView === 'insurance') {
      setStats(prev => ({
        ...prev,
        recoverableDepreciation: insuranceStats.recoverableDepreciation,
        activeSupplements: insuranceStats.activeSupplements,
        supplementsValue: insuranceStats.supplementsValue,
        avgClaimIncrease: insuranceStats.avgClaimIncrease
      }));
    }
  }, [activeView, insuranceStats]);
  const handleScopeUpload = async (file: File) => {
    if (!file) return;
    setAnalyzingScope(true);
    setScopeAnalysis(null);
    try {
      // Read file as text (simplified - in production, use PDF parser)
      const text = await file.text();
      const {
        data,
        error
      } = await supabase.functions.invoke('analyze-adjuster-scope', {
        body: {
          jobId: 'temp',
          documentText: text
        }
      });
      if (error) throw error;
      if (data?.analysis) {
        setScopeAnalysis(data.analysis);
        toast.success('Scope analyzed successfully');
      }
    } catch (error) {
      console.error('Error analyzing scope:', error);
      toast.error('Failed to analyze scope');
    } finally {
      setAnalyzingScope(false);
    }
  };

  // Calculate stats whenever jobs, leads, or workflow changes
  useEffect(() => {
    if (!jobs || !leads) return;

    // Calculate stats
    const activeJobs = jobs.filter(j => ['new', 'contacted', 'scheduled', 'in_progress'].includes(j.status));
    const totalValue = jobs.reduce((sum, j) => sum + (j.estimated_value || 0), 0);

    // Real monthly revenue: jobs created this calendar month
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const monthlyRevenue = jobs
      .filter(j => j.created_at && new Date(j.created_at) >= startOfMonth)
      .reduce((sum, j) => sum + (j.estimated_value || 0), 0);

    // Real close rate: won jobs / total jobs (all-time for the org)
    const wonJobs = jobs.filter(j => j.status === 'won' || j.status === 'completed');
    const closeRate = jobs.length > 0 ? Math.round((wonJobs.length / jobs.length) * 100) : 0;

    // Retail-specific calculations
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const leadsThisWeek = leads.filter(l => new Date(l.created_at) >= oneWeekAgo);
    const leadsLastWeek = leads.filter(l => new Date(l.created_at) >= twoWeeksAgo && new Date(l.created_at) < oneWeekAgo);
    const closedThisWeek = leadsThisWeek.filter(l => l.status === 'won').length;
    const closedLastWeek = leadsLastWeek.filter(l => l.status === 'won').length;
    const closeRateThisWeek = leadsThisWeek.length > 0 ? closedThisWeek / leadsThisWeek.length * 100 : 0;
    const closeRateLastWeek = leadsLastWeek.length > 0 ? closedLastWeek / leadsLastWeek.length * 100 : 0;
    const closeRateTrend: 'up' | 'down' = closeRateThisWeek >= closeRateLastWeek ? 'up' : 'down';
    const avgTicketPrice = jobs.length > 0 ? totalValue / jobs.length : 0;

    // Find leads that received a quote but haven't signed
    const leadsNeedingFollowUp = leads.filter(l => ['contacted', 'qualified', 'proposal_sent'].includes(l.status || '') && l.estimated_value && l.estimated_value > 0).slice(0, 5);

    // Calculate lead sources
    const leadsBySource = leads.reduce((acc, lead) => {
      const source = lead.source || 'Other';
      if (source.toLowerCase().includes('google') || source.toLowerCase().includes('lsa')) {
        acc['Google LSA']++;
      } else if (source.toLowerCase().includes('facebook') || source.toLowerCase().includes('fb')) {
        acc['Facebook']++;
      } else if (source.toLowerCase().includes('canvas') || source.toLowerCase().includes('door')) {
        acc['Canvas']++;
      } else {
        acc['Other']++;
      }
      return acc;
    }, {
      'Google LSA': 0,
      'Facebook': 0,
      'Canvas': 0,
      'Other': 0
    });
    setStats(prev => ({
      ...prev,
      activeProjects: activeJobs.length,
      projectsValue: totalValue,
      pendingProposals: jobs.filter(j => j.status === 'scheduled').length,
      proposalsSent: jobs.filter(j => j.status === 'inspected').length,
      monthlyRevenue,
      closeRate,
      activeCampaigns: leads.filter(l => l.status === 'new').length,
      closeRateThisWeek: Math.round(closeRateThisWeek),
      closeRateTrend,
      avgTicketPrice,
      // financingApprovalRate is intentionally not set here — wire to real data when available
      leadsNeedingFollowUp,
      leadsBySource,
      // Insurance stats are populated by useInsuranceDashboardStats — do NOT override here
    }));

    // Calculate pipeline stats based on workflow stages
    if (workflow && workflow.stages) {
      const stageCounts: Record<string, number> = {};
      workflow.stages.forEach(stage => {
        const jobStatus = stage.job_status;
        const leadStatus = stage.lead_status;
        let count = 0;
        if (jobStatus) {
          count += jobs.filter(j => j.status === jobStatus).length;
        }
        if (leadStatus) {
          count += leads.filter(l => l.status === leadStatus).length;
        }
        stageCounts[stage.stage_key] = count;
      });
      setPipelineStages(stageCounts);
    }
  }, [jobs, leads, workflow]);

  // Commercial portfolio stats - now handled by hook
  useEffect(() => {
    if (!jobs || jobs.length === 0) return;
    const commercialJobs = jobs.filter(j => j.property_type?.toLowerCase() === 'commercial');
    if (commercialJobs.length === 0) {
      setPropertyManagers([]);
      return;
    }

    // Group commercial jobs by property manager/owner
    const managerMap = new Map<string, {
      name: string;
      company: string;
      email: string;
      phone: string;
      properties: number;
      totalValue: number;
    }>();
    commercialJobs.forEach(job => {
      const managerName = job.owner_name || 'Unknown Manager';
      const existing = managerMap.get(managerName);
      if (existing) {
        existing.properties += 1;
        existing.totalValue += job.estimated_value || 0;
      } else {
        managerMap.set(managerName, {
          name: managerName,
          company: job.insurance_company || 'N/A',
          // using insurance_company field as placeholder
          email: job.owner_email || '',
          phone: job.owner_phone || '',
          properties: 1,
          totalValue: job.estimated_value || 0
        });
      }
    });

    // Convert to array and sort by total value
    const managersArray = Array.from(managerMap.values()).sort((a, b) => b.totalValue - a.totalValue).slice(0, 4); // Top 4 managers

    setPropertyManagers(managersArray);
  }, [jobs]);

  // Fetch bid deadlines from calendar events
  useEffect(() => {
    if (!orgId || activeView !== 'commercial') return;
    const fetchBidDeadlines = async () => {
      try {
        const now = new Date();
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 14); // Next 2 weeks

        const {
          data,
          error
        } = await supabase.from('calendar_events').select('*').eq('org_id', orgId).gte('start_time', now.toISOString()).lte('start_time', futureDate.toISOString()).in('event_type', ['bid_deadline', 'walkthrough', 'core_cut', 'inspection']).order('start_time', {
          ascending: true
        }).limit(4);
        if (error) throw error;
        const formattedDeadlines = (data || []).map(event => {
          const deadline = new Date(event.start_time);
          const hoursUntil = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60);
          let urgency = 'low';
          if (hoursUntil <= 6) urgency = 'critical';else if (hoursUntil <= 24) urgency = 'high';else if (hoursUntil <= 48) urgency = 'medium';
          return {
            id: event.id,
            property: event.title,
            type: event.event_type === 'bid_deadline' ? 'Bid Due' : event.event_type === 'core_cut' ? 'Core Cut' : event.event_type === 'walkthrough' ? 'Walkthrough' : 'Inspection',
            deadline,
            urgency,
            value: 0,
            // Could link to job estimated_value if job_id is present
            manager: event.location || 'N/A'
          };
        });
        setBidDeadlines(formattedDeadlines);
      } catch (error) {
        console.error('Error fetching bid deadlines:', error);
      }
    };
    fetchBidDeadlines();
  }, [orgId, activeView]);
  if (authLoading) {
    return <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>;
  }
  if (user && !orgId && !isPlatformOwner) {
    return <OrgSetup />;
  }
  
  // Render mobile dashboard on mobile devices
  if (isMobile) {
    return <MobileDashboard />;
  }
  
  const todayDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const handleStageClick = (status: string) => {
    navigate(`/jobs?status=${status}&view=${activeView}`);
  };
  return <>
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto flex w-full max-w-[1680px] gap-6 px-6 py-6">
        <div className="flex-1 space-y-6">
          <DashboardPanel
            eyebrow="Home"
            title={`Welcome back, ${userName || "there"}`}
            description={todayDate}
            className="bg-gradient-to-r from-card via-card to-primary/5"
            contentClassName="pt-4"
            action={<Button variant="outline" onClick={() => navigate('/jobs')} size="sm" className="h-10 rounded-xl border-border/70 bg-background/80"><Briefcase className="mr-2 h-4 w-4" />All {jobsLabel}</Button>}
          >
            {showSegmentTabs && <div className="flex flex-wrap gap-2">
              {tradeSegments.map((seg) => <Button key={seg.key} variant={activeView === seg.key ? "default" : "outline"} onClick={() => setActiveView(seg.key)} size="sm" className={cn("h-10 rounded-xl border-border/70 px-4", activeView !== seg.key && "bg-background/70")}>{seg.label}</Button>)}
            </div>}
          </DashboardPanel>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <StormWatchWidget />
            </div>
            <SystemHealthWidget />
          </div>

          {statsLoading ? <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div> : <>
            {activeView !== "insurance" && activeView !== "commercial" && <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
                <MetricCard title="Monthly Revenue" value={`$${(stats.monthlyRevenue / 1000).toFixed(0)}K`} icon={DollarSign} onClick={() => navigate('/jobs')} />
                <MetricCard title="Close Rate (7d)" value={`${stats.closeRateThisWeek}%`} icon={Target} trend={{ value: Math.abs(stats.closeRateThisWeek), direction: stats.closeRateTrend }} onClick={() => navigate('/jobs?status=proposal_sent')} />
                <MetricCard title="Active Leads" value={leads.filter(l => l.status === 'new' || l.status === 'contacted').length} icon={Users} onClick={() => navigate('/leads')} />
                <MetricCard title="Avg. Ticket" value={`$${(stats.avgTicketPrice / 1000).toFixed(1)}K`} icon={BarChart3} onClick={() => navigate('/jobs')} />
              </div>
              <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2"><RevenueChart /><PipelineChart /></div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <MetricCard title="Proposal Pending" value={stats.pendingProposals} icon={FileText} onClick={() => navigate('/jobs?status=proposal_sent')} />
                <MetricCard title="Proposals Sent" value={stats.proposalsSent} icon={Mail} onClick={() => navigate('/jobs?status=inspected')} />
              </div>
            </div>}

            {activeView === "insurance" && <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
                <MetricCard title="Recoverable Depreciation" value={`$${(stats.recoverableDepreciation / 1000).toFixed(0)}K`} icon={DollarSign} onClick={() => navigate('/jobs?type=insurance')} />
                <MetricCard title="Active Supplements" value={stats.activeSupplements} icon={FileText} onClick={() => navigate('/jobs?status=supplement')} />
                <MetricCard title="Supplements Value" value={`$${(stats.supplementsValue / 1000).toFixed(0)}K`} icon={TrendingUp} onClick={() => navigate('/jobs')} />
                <MetricCard title="Avg. Claim Increase" value={`${stats.avgClaimIncrease}%`} icon={BarChart3} onClick={() => navigate('/jobs')} />
              </div>
              <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2"><RevenueChart /><PipelineChart /></div>
            </div>}

            {activeView === "commercial" && <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
                <MetricCard title="Active Bid Volume" value={`$${(commercialStats.activeBidVolume / 1000).toFixed(0)}K`} icon={DollarSign} onClick={() => navigate('/jobs?type=commercial&status=proposal_sent')} />
                <MetricCard title="Recurring Revenue" value={`$${(commercialStats.recurringRevenue / 1000).toFixed(0)}K/mo`} icon={TrendingUp} onClick={() => navigate('/jobs?status=maintenance')} />
                <MetricCard title="Win Rate" value={`${commercialStats.winRate.toFixed(0)}%`} icon={Target} onClick={() => navigate('/jobs?type=commercial')} />
                <MetricCard title="Production Backlog" value={`${commercialStats.productionBacklogSquares} sq`} icon={Layers} onClick={() => navigate('/jobs?status=sold')} />
              </div>
              <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2"><RevenueChart /><PipelineChart /></div>
            </div>}

            <DashboardPanel
              eyebrow="Workflow"
              title="Pipeline Stages"
              description="Quick visibility into each active stage"
              action={<Button variant="outline" size="sm" onClick={() => navigate('/workflow-automation')} className="h-10 rounded-xl border-border/70 bg-background/80"><Eye className="mr-2 h-4 w-4" />View Workflow</Button>}
            >
              {workflowLoading ? <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                {workflow?.stages.map(stage => <button key={stage.stage_key} className="rounded-xl border border-border/70 bg-background/75 p-4 text-left transition-all hover:border-primary/40 hover:bg-accent/50" onClick={() => handleStageClick(stage.job_status || stage.stage_key)}>
                  <div className="mb-3 flex items-center gap-2"><div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: stage.color }} /><p className="truncate text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{stage.stage_name}</p></div>
                  <div className="text-3xl font-semibold" style={{ color: stage.color }}>{pipelineStages[stage.stage_key] || 0}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{jobsLabel}</p>
                </button>)}
              </div>}
            </DashboardPanel>
          </>}
        </div>

        <aside className="hidden w-80 shrink-0 space-y-4 xl:block">
          <DailySidebar />
          <TopLeadsCard leads={stats.leadsNeedingFollowUp.map(lead => ({ ...lead, lastContact: new Date(lead.updated_at || lead.created_at || Date.now()) }))} />
          <AIInsightsPanel />
          <RecentActivity />
        </aside>
      </div>
    </div>

      {/* Rebuttal Dialog */}
      <Dialog open={rebuttalDialogOpen} onOpenChange={setRebuttalDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              {selectedRebuttal && <>
                  <div className={cn("p-2 rounded-lg", `bg-${selectedRebuttal.color}/20`)}>
                    {selectedRebuttal.icon && <selectedRebuttal.icon className={cn("w-5 h-5", `text-${selectedRebuttal.color}`)} />}
                  </div>
                  {selectedRebuttal.title}
                </>}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRebuttal && <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-6 font-mono text-sm whitespace-pre-wrap leading-relaxed">
                {selectedRebuttal.rebuttal}
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  Ready-to-send professional rebuttal with code references
                </p>
                <Button onClick={handleCopyRebuttal} className="gap-2" variant={copiedRebuttal ? "secondary" : "default"}>
                  {copiedRebuttal ? <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </> : <>
                      <Copy className="w-4 h-4" />
                      Copy to Clipboard
                    </>}
                </Button>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Pro Tip:</strong> Customize this template with specific details from your claim 
                    (exact product names, dates, photos, etc.) before sending to maximize impact.
                  </p>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
      
      {/* Commercial Action Dialogs */}
      {orgId && <>
          <MaintenanceAgreementDialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen} orgId={orgId} />
          <CoreCutDialog open={coreCutDialogOpen} onOpenChange={setCoreCutDialogOpen} orgId={orgId} />
          <SafetyPlanDialog open={safetyPlanDialogOpen} onOpenChange={setSafetyPlanDialogOpen} orgId={orgId} />
        </>}
    </>;
};
export default Dashboard;